// Cmd+K command palette. Mixes static page targets with fuzzy-searched
// users, sims and courses pulled from the mock data. Selecting a result
// drills the shell to the matching page with the appropriate scope
// context already set.
//
// Calculations: substring (lowercased) match over name + email for users,
// over name for sims and courses. Results trimmed to a small per-section
// cap to keep the dropdown responsive.
//
// Postgres mapping: needs server-side search endpoint
// `useCommandSearchQuery(q)` returning union of users / sims / courses
// matching across (name, email, tags). Local in-memory match works only
// while the data set is small; >10k users requires Postgres full-text
// search or trigram index on `users.name`, `users.email`.
//
// Integration hook: `useCommandSearchQuery` against a new
// `/api/search` endpoint; cache 30 seconds, debounce 150ms.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import FlagIcon from '@mui/icons-material/FlagOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import IosShareIcon from '@mui/icons-material/IosShareOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import TableChartIcon from '@mui/icons-material/TableChartOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import EmailIcon from '@mui/icons-material/MailOutline';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import LocationIcon from '@mui/icons-material/LocationOnOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLateOutlined';
import LeaderboardIcon from '@mui/icons-material/LeaderboardOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import { SIMS, USERS, DEPTS, LOCS } from '../data';
import { FEATURES } from '../featureFlags';

// Jump-to-anywhere palette. Two design decisions worth recording:
//
//   1. The corpus is built once when the palette opens (not on every
//      keystroke). 200 sims + 800 users + a handful of pivots is small
//      enough that linear scoring on each input change is fine, but the
//      corpus shape doesn't change per character.
//
//   2. Matches are scored, not just filtered. A "Sam" query should put the
//      user "Sam McDougall" above sims that happen to contain "sam" in
//      their tag list. Scoring rules: starts-with > word-boundary > anywhere;
//      label > secondary text. The ordering is then split back into
//      categories for display because grouping helps the eye.

// Static navigation targets. `page` is the page id passed to go(); `id` is a
// unique key for the palette (factors share the 'factor-detail' page so they
// need distinct ids). `context` is forwarded to go() for pages that render
// from a scope/value. `keywords` widens search beyond the visible label so a
// page can be reached by synonyms it does not literally show (e.g. "overdue"
// finds Outstanding users, "deliverability" finds Send history).
const PAGE_ENTRIES = [
    { id: 'summary',        label: 'Home',                hint: 'Risk score and drivers',                 icon: HomeIcon,        keywords: 'dashboard overview risk score drivers home start' },
    { id: 'sims',           label: 'Simulations',         hint: 'All sim activity',                       icon: BugReportIcon,   keywords: 'phishing simulations campaigns clicks tests sends' },
    { id: 'flagged-sims',   label: 'Flagged simulations', hint: 'Sims needing review',                    icon: FlagIcon,        feature: 'flaggedSimulations', keywords: 'flagged reported suspicious review attention' },
    { id: 'courses',        label: 'Courses',             hint: 'Training assignments and completion',    icon: SchoolIcon,      keywords: 'training learning courses completion assignments education' },
    { id: 'users',          label: 'Users',               hint: 'All users',                              icon: GroupIcon,       keywords: 'people employees recipients staff members directory' },
    { id: 'dwm',            label: 'Dark web',            hint: 'Breach exposure feed',                   icon: ShieldIcon,      feature: 'darkWebMonitoring', keywords: 'dark web breach credentials exposure leaked compromised monitoring' },
    { id: 'export-builder',   label: 'Export builder',    hint: 'Compose a custom export',  icon: IosShareIcon,    feature: 'exportBuilder', keywords: 'export download report builder compose custom csv excel pdf' },
    { id: 'export-scheduled', label: 'Scheduled exports', hint: 'Automated deliveries',     icon: ScheduleIcon,    feature: 'exportBuilder', keywords: 'scheduled automated deliveries recurring exports subscriptions' },
    { id: 'export-tables',    label: 'Data tables',       hint: 'Download full tables',      icon: TableChartIcon,  feature: 'exportBuilder', keywords: 'data tables download raw full export grid' },
    { id: 'export-history',   label: 'Export history',    hint: 'Exports you have pulled',   icon: DescriptionIcon, feature: 'exportBuilder', keywords: 'export history downloads past pulled previous' },
    { id: 'risk-methodology', label: 'Risk methodology',  hint: 'How the score is calculated',            icon: ShieldIcon,      keywords: 'methodology how score calculated formula weighting factors explained' },

    // Drilldown pages: standalone analysis pages normally reached by clicking
    // through a card. Surfaced here so they are directly searchable.
    { id: 'dept',              label: 'League tables',    hint: 'Score drivers ranked by group', icon: LeaderboardIcon, keywords: 'league tables group breakdown score driver factor department location country company city state team ranking' },
    { id: 'send-history',      label: 'Send history',     hint: 'Simulation delivery log',  icon: SendIcon,            keywords: 'send history delivery log sent emails deliverability dispatched' },
    { id: 'course-send-log',   label: 'Course send log',  hint: 'Course delivery log',      icon: HistoryIcon,         keywords: 'course send log training delivery assignments sent enrolments' },
    { id: 'high-risk-users',   label: 'High risk users',  hint: 'Repeat clickers and high risk', icon: WarningIcon,    keywords: 'high risk users high click repeat clickers offenders worst riskiest dangerous' },
    { id: 'outstanding-users', label: 'Outstanding users', hint: 'Overdue training',        icon: AssignmentLateIcon,  keywords: 'outstanding overdue incomplete pending not completed unfinished training nudge' },

    // Risk factors: each routes into the shared factor-detail page with the
    // factor id as context, so the five score drivers are individually searchable.
    { id: 'factor:fail-rate',     page: 'factor-detail', label: 'Fail rate',            hint: 'Risk factor',  icon: InsightsIcon, context: { type: 'factor', value: 'fail-rate' },     category: 'Risk factors', keywords: 'fail rate failed phishing tests driver factor score' },
    { id: 'factor:repeat',        page: 'factor-detail', label: 'Repeat offender rate', hint: 'Risk factor',  icon: InsightsIcon, context: { type: 'factor', value: 'repeat' },        category: 'Risk factors', keywords: 'repeat offender streak consecutive clicks driver factor score' },
    { id: 'factor:stage-depth',   page: 'factor-detail', label: 'Stage depth',          hint: 'Risk factor',  icon: InsightsIcon, context: { type: 'factor', value: 'stage-depth' },   category: 'Risk factors', keywords: 'stage depth credentials data entry how far driver factor score' },
    { id: 'factor:training-debt', page: 'factor-detail', label: 'Training debt',        hint: 'Risk factor',  icon: InsightsIcon, context: { type: 'factor', value: 'training-debt' }, category: 'Risk factors', keywords: 'training debt overdue incomplete courses backlog driver factor score' },
    { id: 'factor:course-lag',    page: 'factor-detail', label: 'Course lag',           hint: 'Risk factor',  icon: InsightsIcon, context: { type: 'factor', value: 'course-lag' },    category: 'Risk factors', keywords: 'course lag time to complete delay slow driver factor score' },
];

// Score how well `q` matches `text`. Higher is better; 0 = no match.
function score(text, q) {
    if (!text) return 0;
    const t = text.toLowerCase();
    const i = t.indexOf(q);
    if (i === -1) return 0;
    if (i === 0) return 100;                          // starts-with
    if (/[\s\-_/]/.test(t.charAt(i - 1))) return 70;  // word boundary
    return 30;                                         // somewhere inside
}

function scoreEntry(entry, q) {
    if (!q) return entry.weight ?? 1;
    const primary  = score(entry.label, q);
    const secondary = entry.searchExtra ? score(entry.searchExtra, q) * 0.6 : 0;
    return primary + secondary;
}

function MatchText({ text, q }) {
    if (!q) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return <>{text}</>;
    return (
        <>
            {text.slice(0, idx)}
            <Box component="mark" sx={{
                background: 'transparent',
                color: 'secondary.main',
                fontWeight: 700,
                padding: 0,
            }}>
                {text.slice(idx, idx + q.length)}
            </Box>
            {text.slice(idx + q.length)}
        </>
    );
}

export default function CommandPalette({ open, onClose, go, drill, dataVersion = 0 }) {
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Reset state on close so a returning user sees an empty palette and
    // their cursor parked in the search field.
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIdx(0);
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    // Build the searchable corpus. Pivots (Sims, Users, Departments,
    // Locations) get a category label so they group cleanly in the result
    // list. Page entries get a "Page" category so the first-encountered
    // navigation destinations stay on top.
    const corpus = useMemo(() => {
        const pages = PAGE_ENTRIES
            .filter((p) => !p.feature || FEATURES[p.feature])
            .map((p) => ({
                kind: 'page',
                category: p.category || 'Pages',
                id: p.id,
                label: p.label,
                hint: p.hint,
                icon: p.icon,
                searchExtra: p.keywords,
                action: ({ go }) => go(p.page || p.id, p.label, p.context || null),
                weight: 5,
            }));
        const sims = SIMS.map((s) => ({
            kind: 'sim',
            category: 'Simulations',
            id: `sim:${s.name}`,
            label: s.name,
            hint: `${s.click}% click rate - ${s.lv} risk`,
            searchExtra: `${s.click} click ${s.lv}`,
            icon: BugReportIcon,
            action: ({ drill }) => drill('sim-detail', s.name, { type: 'sim', value: s.name }),
            weight: 1,
        }));
        const users = USERS.slice(0, 400).map((u) => ({
            kind: 'user',
            category: 'Users',
            id: `user:${u.email}`,
            label: u.name,
            hint: `${u.email} - ${u.dept}`,
            searchExtra: `${u.email} ${u.dept}`,
            icon: u.email ? EmailIcon : PersonIcon,
            action: ({ drill }) => drill('user-detail', u.name, { type: 'user', value: u.email }),
            weight: 1,
        }));
        const depts = DEPTS.map((d) => ({
            kind: 'dept',
            category: 'Departments',
            id: `dept:${d.name}`,
            label: d.name,
            hint: `${d.users} users - ${d.click}% click rate`,
            icon: BusinessIcon,
            action: ({ drill }) => drill('sims', `Sims - ${d.name}`, { type: 'department', value: d.name }),
            weight: 1,
        }));
        const locs = LOCS.map((l) => ({
            kind: 'loc',
            category: 'Locations',
            id: `loc:${l.name}`,
            label: l.name,
            hint: `${l.users} users - ${l.click}% click rate`,
            icon: LocationIcon,
            action: ({ drill }) => drill('sims', `Sims - ${l.name}`, { type: 'location', value: l.name }),
            weight: 1,
        }));
        return [...pages, ...sims, ...users, ...depts, ...locs];
        // dataVersion: rebuilt after each data hydration.
    }, [dataVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    // Score and rank. When the query is empty we show pages only so the
    // palette doubles as a quick nav surface; once the user starts typing
    // we open the corpus up to everything.
    //
    // The corpus indexes every sim, user, department and location for
    // jump-to, so a broad query can match hundreds of near-identical
    // records. Left unbounded they flood the list and, because the display
    // groups sequentially, drop a category header before almost every row.
    // So after scoring we bucket by category in a fixed order and cap the
    // open-ended record categories, navigation targets (pages, factors)
    // stay first and uncapped; sims/users/etc. show only their top matches.
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return corpus.filter((e) => e.kind === 'page');
        }
        const scored = [];
        for (const e of corpus) {
            const s = scoreEntry(e, q);
            if (s > 0) scored.push({ entry: e, score: s });
        }
        scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label));

        const CATEGORY_ORDER = ['Pages', 'Risk factors', 'Simulations', 'Users', 'Departments', 'Locations'];
        const CATEGORY_CAP = { Pages: Infinity, 'Risk factors': Infinity };
        const RECORD_CAP = 5; // per open-ended record category

        const byCategory = new Map();
        for (const { entry } of scored) {
            if (!byCategory.has(entry.category)) byCategory.set(entry.category, []);
            byCategory.get(entry.category).push(entry);
        }
        const out = [];
        for (const cat of CATEGORY_ORDER) {
            const list = byCategory.get(cat);
            if (!list) continue;
            const cap = CATEGORY_CAP[cat] ?? RECORD_CAP;
            out.push(...list.slice(0, cap));
        }
        return out.slice(0, 50);
    }, [query, corpus]);

    // Group sequentially so the category header changes track ranking
    // order rather than imposing a fixed top-down category arrangement.
    const grouped = useMemo(() => {
        const out = [];
        let lastCategory = null;
        for (let i = 0; i < results.length; i++) {
            const r = results[i];
            if (r.category !== lastCategory) {
                out.push({ type: 'header', category: r.category });
                lastCategory = r.category;
            }
            out.push({ type: 'item', entry: r, index: i });
        }
        return out;
    }, [results]);

    // Clamp active index to result length whenever results shift.
    useEffect(() => {
        setActiveIdx((idx) => Math.max(0, Math.min(idx, results.length - 1)));
    }, [results.length]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((idx) => Math.min(idx + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((idx) => Math.max(idx - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const r = results[activeIdx];
            if (r) {
                r.action({ go, drill });
                onClose();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    // Scroll the active item into view when navigation hits the edge of the
    // visible window.
    useEffect(() => {
        if (!listRef.current) return;
        const node = listRef.current.querySelector(`[data-rbx-cmd-idx="${activeIdx}"]`);
        if (node) node.scrollIntoView({ block: 'nearest' });
    }, [activeIdx]);

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed', inset: 0,
                            background: 'rgba(15,18,25,0.45)',
                            backdropFilter: 'blur(2px)',
                            zIndex: 1400,
                        }}
                    />
                    <motion.div
                        key="palette"
                        initial={{ opacity: 0, scale: 0.97, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97, y: -8 }}
                        transition={{ duration: 0.16, ease: 'easeOut' }}
                        style={{
                            position: 'fixed',
                            top: '12vh',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'min(640px, calc(100vw - 32px))',
                            maxHeight: '70vh',
                            zIndex: 1401,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{
                            bgcolor: 'background.paper',
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: '12px',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.22), 0 2px 12px rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            flex: 1,
                        }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 2, py: 1.5,
                                borderBottom: '1px solid', borderColor: 'divider',
                            }}>
                                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                <Box
                                    component="input"
                                    ref={inputRef}
                                    value={query}
                                    onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Jump to a page, sim, user, department..."
                                    sx={{
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        color: 'text.primary',
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: 14,
                                        '&::placeholder': { color: 'text.disabled' },
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                    color: 'text.disabled', textTransform: 'uppercase',
                                }}>
                                    ESC to close
                                </Typography>
                            </Box>

                            <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5 }}>
                                {grouped.length === 0 && (
                                    <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
                                            No matches for &ldquo;{query}&rdquo;
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                            Try a sim name, user email, department or page label.
                                        </Typography>
                                    </Box>
                                )}
                                {grouped.map((row) => {
                                    if (row.type === 'header') {
                                        return (
                                            <Typography
                                                key={`h-${row.category}`}
                                                sx={{
                                                    fontSize: 9, fontWeight: 700,
                                                    textTransform: 'uppercase', letterSpacing: '0.07em',
                                                    color: 'text.disabled',
                                                    px: 2, pt: 1, pb: 0.5,
                                                }}
                                            >
                                                {row.category}
                                            </Typography>
                                        );
                                    }
                                    const e = row.entry;
                                    const isActive = row.index === activeIdx;
                                    const Icon = e.icon;
                                    return (
                                        <Box
                                            key={e.id}
                                            data-rbx-cmd-idx={row.index}
                                            onMouseEnter={() => setActiveIdx(row.index)}
                                            onClick={() => { e.action({ go, drill }); onClose(); }}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.25,
                                                mx: 1, px: 1.25, py: 1,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                bgcolor: isActive ? 'action.selected' : 'transparent',
                                                transition: 'background 80ms',
                                            }}
                                        >
                                            {Icon && (
                                                <Icon sx={{
                                                    fontSize: 16,
                                                    color: isActive ? 'secondary.main' : 'text.disabled',
                                                    flexShrink: 0,
                                                }} />
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <Typography sx={{
                                                    fontSize: 12.5, fontWeight: 600,
                                                    color: 'text.primary',
                                                    lineHeight: 1.3,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    <MatchText text={e.label} q={query.trim().toLowerCase()} />
                                                </Typography>
                                                {e.hint && (
                                                    <Typography sx={{
                                                        fontSize: 10.5,
                                                        color: 'text.secondary',
                                                        lineHeight: 1.3,
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}>
                                                        {e.hint}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {isActive && (
                                                <Typography sx={{
                                                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                                    color: 'text.disabled', textTransform: 'uppercase',
                                                    flexShrink: 0,
                                                }}>
                                                    Enter
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                px: 2, py: 1,
                                borderTop: '1px solid', borderColor: 'divider',
                                bgcolor: 'action.hover',
                            }}>
                                <Hint label="navigate" keys={['↑', '↓']} />
                                <Hint label="open"     keys={['Enter']} />
                                <Hint label="close"    keys={['esc']} />
                                <Typography sx={{
                                    fontSize: 9, color: 'text.disabled',
                                    marginLeft: 'auto',
                                }}>
                                    {results.length} {results.length === 1 ? 'result' : 'results'}
                                </Typography>
                            </Box>
                        </Box>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

function Hint({ label, keys }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {keys.map((k) => (
                <Box key={k} component="kbd" sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: '3px',
                    padding: '1px 5px',
                }}>{k}</Box>
            ))}
            <Typography sx={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', color: 'text.disabled', textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
}
